/**
 * Work-order fields a WhatsApp template placeholder can be filled from (#4106).
 *
 * The canonical list lives in `packages/shared/src/schemas/whatsapp.ts`
 * (`WHATSAPP_TEMPLATE_PARAMETER_SOURCES`). Portal apps must not import
 * `packages/` or `@vehiclespace/*`, so this file mirrors that list. Keep the
 * keys and their order identical; `whatsappTemplateSources.test.ts` checks.
 */

import { formatCentsForDisplay } from '@/utils/money';

export const WHATSAPP_TEMPLATE_PARAMETER_SOURCES = [
  'registration_number',
  'work_order_number',
  'total_with_vat',
  'customer_name',
  'workshop_name',
  'pickup_time',
] as const;

export type WhatsAppTemplateParameterSource =
  (typeof WHATSAPP_TEMPLATE_PARAMETER_SOURCES)[number];

export function isWhatsAppTemplateParameterSource(
  value: unknown,
): value is WhatsAppTemplateParameterSource {
  return (
    typeof value === 'string' &&
    (WHATSAPP_TEMPLATE_PARAMETER_SOURCES as readonly string[]).includes(value)
  );
}

/** Values taken from the work order linked to the conversation. */
export interface WhatsAppTemplateParameterContext {
  readonly registrationNumber: string | null;
  readonly workOrderNumber: string | null;
  readonly totalWithVatCents: number | null;
  readonly customerName: string | null;
  readonly workshopName: string | null;
  /** ISO timestamp of the customer-requested pickup, or null. */
  readonly pickupTime: string | null;
}

export function templateParameterContextFromWorkOrder(input: {
  registrationNumber: string | null;
  workOrderNumber: string | null | undefined;
  totalWithVatCents: number | null | undefined;
  totalCostCents: number | null | undefined;
  customerName: string | null;
  workshopName: string | null;
  pickupTime: string | null;
}): WhatsAppTemplateParameterContext {
  return {
    registrationNumber: input.registrationNumber,
    workOrderNumber: input.workOrderNumber ?? null,
    totalWithVatCents: input.totalWithVatCents ?? input.totalCostCents ?? null,
    customerName: input.customerName,
    workshopName: input.workshopName,
    pickupTime: input.pickupTime,
  };
}

function formatPickupTime(value: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('fi-FI', { dateStyle: 'short', timeStyle: 'short' });
}

function valueForSource(
  source: WhatsAppTemplateParameterSource,
  ctx: WhatsAppTemplateParameterContext,
): string {
  switch (source) {
    case 'registration_number':
      return ctx.registrationNumber?.trim() ?? '';
    case 'work_order_number':
      return ctx.workOrderNumber?.trim() ?? '';
    case 'total_with_vat':
      return ctx.totalWithVatCents == null || !Number.isFinite(ctx.totalWithVatCents)
        ? ''
        : formatCentsForDisplay(ctx.totalWithVatCents);
    case 'customer_name':
      return ctx.customerName?.trim() ?? '';
    case 'workshop_name':
      return ctx.workshopName?.trim() ?? '';
    case 'pickup_time':
      return formatPickupTime(ctx.pickupTime);
    default:
      return '';
  }
}

/**
 * One string per source entry, in placeholder order. A null source, a missing
 * context, or a missing value becomes `''` so the field stays editable and
 * empty. An unmapped template (`sources` null) returns no values.
 */
export function resolveTemplateParameters(
  sources: readonly (string | null)[] | null | undefined,
  ctx: WhatsAppTemplateParameterContext | null | undefined,
): string[] {
  if (!sources || sources.length === 0) return [];
  return sources.map((source) => {
    if (!ctx || !isWhatsAppTemplateParameterSource(source)) return '';
    return valueForSource(source, ctx);
  });
}
